import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function normalizeMinus(s) {
  return String(s ?? "").replace(/[−–]/g, "-");
}

function buildLatexResult(coeffs, root) {
  const parts = [];
  const push = (coef, body) => {
    if (coef === 0) return;
    const sign = coef < 0 ? "-" : "+";
    const abs = Math.abs(coef);
    const coefTxt = abs === 1 && body ? "" : `${abs}`;
    parts.push({ sign, txt: `${coefTxt}${body}` });
  };

  push(coeffs.rootOnly, `\\sqrt{${root}}`);
  push(coeffs.c, "");

  if (!parts.length) return "0";
  const [first, ...rest] = parts;
  const firstTxt = `${first.sign === "-" ? "-" : ""}${first.txt}`;
  return `${firstTxt}${rest.map((p) => ` ${p.sign} ${p.txt}`).join("")}`;
}

function parseAnswer(raw, root) {
  const s = normalizeMinus(raw)
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/\*/g, "")
    .replace(/\\sqrt\{(\d+)\}/g, "sqrt$1")
    .replace(/\\sqrt(\d+)/g, "sqrt$1")
    .replace(/sqrt\((\d+)\)/g, "sqrt$1")
    .replace(/√\((\d+)\)/g, "sqrt$1")
    .replace(/√(\d+)/g, "sqrt$1");

  if (!s) return null;

  const norm = s[0] === "-" ? s : `+${s}`;
  const terms = norm.match(/[+-][^+-]+/g);
  if (!terms) return null;

  const out = { rootOnly: 0, c: 0 };
  const targetRoot = `sqrt${root}`;

  const parseCoeff = (txt) => {
    if (txt === "" || txt === "+") return 1;
    if (txt === "-") return -1;
    const n = Number(txt);
    return Number.isFinite(n) ? n : null;
  };

  for (const term of terms) {
    if (term.includes(targetRoot)) {
      const coeff = parseCoeff(term.replace(targetRoot, ""));
      if (coeff === null) return null;
      out.rootOnly += coeff;
      continue;
    }
    const n = Number(term);
    if (!Number.isFinite(n)) return null;
    out.c += n;
  }

  return out;
}

function makeComponent(kind, root) {
  const a = randInt(1, 9);
  const b = randInt(1, 4);
  const bsqrt = b === 1 ? `\\sqrt{${root}}` : `${b}\\sqrt{${root}}`;

  if (kind === "sumSquare") {
    return {
      kind,
      expression: `(${a} + ${bsqrt})^2`,
      expanded: {
        rootOnly: 2 * a * b,
        c: a * a + b * b * root,
      },
      formula: "(a+b)^2=a^2+2ab+b^2",
    };
  }

  if (kind === "diffSquare") {
    return {
      kind,
      expression: `(${a} - ${bsqrt})^2`,
      expanded: {
        rootOnly: -2 * a * b,
        c: a * a + b * b * root,
      },
      formula: "(a-b)^2=a^2-2ab+b^2",
    };
  }

  return {
    kind,
    expression: `(${a} - ${bsqrt})(${a} + ${bsqrt})`,
    expanded: {
      rootOnly: 0,
      c: a * a - b * b * root,
    },
    formula: "(a-b)(a+b)=a^2-b^2",
  };
}

export function generateTask() {
  const kinds = ["sumSquare", "diffSquare", "diffSquares"];
  const root = pick([2, 3, 5, 7]);

  for (let i = 0; i < 30; i += 1) {
    const leftKind = pick(kinds);
    const rightKind = pick(kinds.filter((k) => k !== leftKind));
    const left = makeComponent(leftKind, root);
    const right = makeComponent(rightKind, root);

    const op = Math.random() < 0.5 ? "+" : "-";
    const sign = op === "+" ? 1 : -1;

    const result = {
      rootOnly: left.expanded.rootOnly + sign * right.expanded.rootOnly,
      c: left.expanded.c + sign * right.expanded.c,
    };

    if (
      left.expression === right.expression ||
      (result.rootOnly === 0 && result.c === 0)
    ) {
      continue;
    }

    return {
      type: "MixedSpecialProductsSqrtTask",
      data: {
        root,
        left,
        right,
        op,
        expectedCoeffs: result,
        expected: buildLatexResult(result, root),
        expression: `${left.expression} ${op} ${right.expression}`,
      },
    };
  }

  const left = makeComponent("sumSquare", root);
  const right = makeComponent("diffSquares", root);
  const result = {
    rootOnly: left.expanded.rootOnly + right.expanded.rootOnly,
    c: left.expanded.c + right.expanded.c,
  };

  return {
    type: "MixedSpecialProductsSqrtTask",
    data: {
      root,
      left,
      right,
      op: "+",
      expectedCoeffs: result,
      expected: buildLatexResult(result, root),
      expression: `${left.expression} + ${right.expression}`,
    },
  };
}

export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <InlineMath math={data.expression} />
    </p>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <SqrtInput
      value={typeof userAnswer === "string" ? userAnswer : userAnswer?.raw || ""}
      onChange={onChange}
      disabled={disabled}
      wide={true}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw || "";
  if (!raw || typeof raw !== "string") {
    return { isCorrect: false, expected: data.expected };
  }

  const parsed = parseAnswer(raw, data.root);
  if (!parsed) return { isCorrect: false, expected: data.expected };

  const e = data.expectedCoeffs;
  const ok = parsed.rootOnly === e.rootOnly && parsed.c === e.c;

  return { isCorrect: ok, expected: data.expected };
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.expected} />;
}

export function renderExplanation({ data }) {
  const leftExpanded = buildLatexResult(data.left.expanded, data.root);
  const rightExpanded = buildLatexResult(data.right.expanded, data.root);
  const expected = data.expected;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Rozpoznajemy wzory:
      </p>
      <BlockMath math={`${data.left.formula}\\qquad ${data.right.formula}`} />
      <p>
        <strong>Krok 2:</strong> Rozwijamy oba składniki:
      </p>
      <BlockMath
        math={`${data.left.expression}=${leftExpanded}\\qquad ${data.right.expression}=${rightExpanded}`}
      />
      <p>
        <strong>Krok 3:</strong> Podstawiamy i redukujemy:
      </p>
      <BlockMath math={`${data.expression}=(${leftExpanded}) ${data.op} (${rightExpanded})=${expected}`} />
    </div>
  );
}

export function renderUserAnswer(...args) {
  const ua = args.length === 1 ? args[0] : args[1];
  const raw = typeof ua === "string" ? ua : ua?.raw;
  if (!raw || String(raw).trim() === "") return <i>(brak odpowiedzi)</i>;
  const latex = normalizeMinus(raw)
    .replace(/\s+/g, "")
    .replace(/\*/g, "")
    .replace(/sqrt\((\d+)\)/gi, "\\sqrt{$1}")
    .replace(/√\((\d+)\)/g, "\\sqrt{$1}")
    .replace(/√(\d+)/g, "\\sqrt{$1}");
  return <InlineMath math={latex} />;
}
